import { useState } from 'react'

import {
  Box,
  BoxList,
  Button,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { formatTime } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import type Document from 'modules/documents/store/models/Document'
import { RESULT_ACCEPTED_MIME_TYPES } from 'modules/payroll_bundle/lib/constants'
import { documentAuthor } from 'modules/payroll_bundle/lib/utils'

interface ToolTipProps {
  createdAt: string
  authorName: string
}

const TooltipDialog = ({ createdAt, authorName }: ToolTipProps) => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.ItemList open={open} toggle={toggle} attachment="top center">
      <Icon.Medium icon="info-circle-line" color="grey800" />
      <Box alignItems="flexStart">
        <Text.Normal size="200" capitalize>
          {i18n.t('payroll_bundle.payroll_results.file_item.upload_title').toUpperCase()}
        </Text.Normal>
        <Text.Normal size="200">
          {i18n.t('payroll_bundle.payroll_results.file_item.upload_description', {
            createdAt,
            authorName,
          })}
        </Text.Normal>
      </Box>
    </Tooltip.ItemList>
  )
}

interface Props {
  document: Document
  documentStatus: string
  onProcessResult: (documentId: number) => void
}

const FileItem = ({ document, documentStatus, onProcessResult }: Props) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const date = moment.unix(document.attributes.ar_created_at)
  const currentAccess = useCurrentSession().access
  const time = formatTime(currentAccess, date.format('HH:mm'))
  const createdAt = `${date.format('ll')}, ${time}`
  const fileName = document.attributes.filename
  const fileAuthor = documentAuthor(document)
  const authorName = fileAuthor?.fullName()
  const invalidDocument = !RESULT_ACCEPTED_MIME_TYPES.includes(document.get('content_type'))

  const handleOpenTooltip = () => {
    setShowTooltip(true)
  }

  const handleCloseTooltip = () => {
    setShowTooltip(false)
  }

  const handleOnClick = () => {
    onProcessResult(document.id)
  }

  return (
    <BoxList>
      <BoxList.Item>
        <Box width="full">
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            paddingBottom="s16"
          >
            <Box
              alignItems="center"
              background={'champagne200'}
              borderRadius={{ all: 'abs008' }}
              paddingX="s8"
              paddingY="s2"
            >
              <Text.Normal weight="medium" color="grey1000">
                {documentStatus}
              </Text.Normal>
            </Box>
            <Tooltip.Alt
              attachment="top center"
              label={i18n.t('payroll_bundle.payroll_results.result_files.invalid_file_format')}
              open={showTooltip && invalidDocument}
            >
              <Box onMouseEnter={handleOpenTooltip} onMouseLeave={handleCloseTooltip}>
                <Button type="secondary" onClick={handleOnClick} disabled={invalidDocument}>
                  {i18n.t('payroll_bundle.payroll_results.file_item.process_file')}
                </Button>
              </Box>
            </Tooltip.Alt>
          </Box>
          <Text.Normal size="300" weight="medium" color="grey800" ellipsis>
            {fileName}
          </Text.Normal>
          <Box flexDirection="row" alignItems="center" justifyContent="flexStart">
            <Text.Normal size="200">
              {i18n.t('payroll_bundle.payroll_results.file_item.uploaded_subtitle', {
                createdAt,
              })}
            </Text.Normal>
            <TooltipDialog createdAt={createdAt} authorName={authorName || ''} />
          </Box>
        </Box>
      </BoxList.Item>
    </BoxList>
  )
}

export default FileItem
